/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaBelongsToLevelBox.tsx
 * FILE_ID: JOBHUB-QA-BELONGS-LEVEL-001
 * PURPOSE: Type or pick the level for the locations the user already chose.
 */
import { useState } from 'react'
import type { QaArea, QaFloor } from '../../qa/qaApi'
import { levelsOf } from './qaTabSelectors'

export type QaLevelDestination = { parentId: string } | { newLevelName: string }

function sameName(left: string, right: string) {
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaMoveDialog.tsx (accent-insensitive name match)
  return left.localeCompare(right, undefined, { sensitivity: 'accent' }) === 0
}

export function QaBelongsToLevelBox({
  floors,
  areas,
  otherLevelNames,
  busy = false,
  onCancel,
  onSubmit,
}: {
  floors: QaFloor[]
  areas: QaArea[]
  otherLevelNames: string[]
  busy?: boolean
  onCancel: () => void
  onSubmit: (destination: QaLevelDestination) => void
}) {
  const [name, setName] = useState('')
  const [locationsOpen, setLocationsOpen] = useState(false)
  const levels = levelsOf(floors, areas)
  const defaultFloorId = floors[0]?.id
  const knownNames = floors.map((floor) => floor.floor_name)
  const extraNames = otherLevelNames.filter((levelName) => !knownNames.some((floorName) => sameName(floorName, levelName)))

  const submitName = () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    const floor = floors.find((item) => sameName(item.floor_name, trimmed))
    onSubmit(floor ? { parentId: floor.id } : { newLevelName: trimmed })
  }

  return (
    <div className="mb-1 rounded-lg border border-border bg-card p-2" data-testid="qa-belongs-level">
      <p className="px-1 pb-1.5 text-[11px] font-semibold text-foreground">Belongs to Level:</p>
      <input
        type="text"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel()
          if (event.key !== 'Enter') return
          event.preventDefault()
          submitName()
        }}
        placeholder="Basement"
        aria-label="Level name"
        data-testid="qa-belongs-level-name"
        className="mb-1.5 h-7 w-full rounded-md border border-border bg-background px-2 text-[11px] text-foreground"
      />
      <button
        type="button"
        className="flex w-full items-center gap-1 px-1 py-1 text-left text-[11px] font-semibold text-muted-foreground"
        aria-expanded={locationsOpen}
        data-testid="qa-belongs-locations-toggle"
        onClick={() => setLocationsOpen((open) => !open)}
      >
        <span>{locationsOpen ? 'v' : '>'}</span>
        Locations
      </button>
      {locationsOpen ? (
        <ul className="max-h-[45vh] overflow-auto" data-testid="qa-belongs-locations">
          {levels.map(({ floor, areas: levelAreas }) => {
            const grouped = Boolean(floor && floor.id !== defaultFloorId)
            return (
              <li key={floor?.id ?? 'unfiled'}>
                {grouped && floor ? (
                  <button
                    type="button"
                    disabled={busy}
                    data-testid={`qa-belongs-level-${floor.id}`}
                    className="flex w-full rounded-md px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
                    onClick={() => onSubmit({ parentId: floor.id })}
                  >
                    {floor.floor_name}
                  </button>
                ) : null}
                <ul>
                  {levelAreas.map((area) => (
                    <li
                      key={area.id}
                      data-testid={`qa-belongs-location-${area.id}`}
                      className={grouped ? 'py-1 pl-6 text-[11px] text-foreground' : 'px-2 py-1 text-[11px] text-foreground'}
                    >
                      {area.area_name}
                    </li>
                  ))}
                </ul>
              </li>
            )
          })}
          {extraNames.map((levelName) => (
            <li key={levelName}>
              <button
                type="button"
                disabled={busy}
                data-testid={`qa-belongs-other-${levelName}`}
                className="flex w-full rounded-md px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
                onClick={() => onSubmit({ newLevelName: levelName })}
              >
                {levelName}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
